'use client'

import dynamic from 'next/dynamic'
import { useRef, useState } from 'react'
import { SwiperRef } from 'swiper/react'
import { aspectRatioClass } from './configuration'
import { Media, OrMedia } from '../../organisms'

// The multi-image carousel path pulls in Swiper (and its CSS) — only needed when
// there is more than one image/media item. Code-splitting it keeps Swiper out of
// the main bundle for every single-image usage (including hero/LCP images).
// ssr stays enabled (the default) so carousels still render on first paint — only
// the Swiper JS chunk itself is deferred/loaded in parallel, avoiding CLS.
const SwiperEngine = dynamic(() => import('./SwiperEngine').then((mod) => mod.SwiperEngine))

export type AspectRatio = '16:9' | '1:1' | '2:1' | '3:2' | '4:3' | '3:4' | '5:6' | 'custom'

type SizeOptions = {
  aspectRatio?: AspectRatio
  /** Manually set max dimensions, if an aspect ratio is set, by just setting either width or height browser will calculate what the other should be */
  unsetMaxDimensions?: boolean
  /** Change the dimension of the image, this will only work if aspect ratio 1:1 want to use the other width */
  changeDimension?: boolean
}

export type Image = {
  src: string
  alt: string
  caption?: string
  srcset?: string
  sizes?: string
  width?: number
  height?: number
}

export type Images = {
  images?: Image[]
}

export type MlImagesProps = Images &
  SizeOptions & {
    media?: Media[]
    onSlideChange?: (index: number) => void
    onClick?: (index: number) => void
    href?: string
    className?: string
    /** When true, images use loading="lazy" and fetchPriority="auto" to defer off-screen images */
    lazy?: boolean
  }

export const MlImages = ({
  media = [],
  images,
  aspectRatio = 'custom',
  unsetMaxDimensions = false,
  changeDimension = false,
  onSlideChange,
  onClick,
  href,
  className = '',
  lazy = false,
}: MlImagesProps) => {
  const swiper = useRef<SwiperRef | null>(null)
  const [beginning, setBeginning] = useState(true)
  const [end, setEnd] = useState(false)

  const dimensions = aspectRatio === '1:1' && !changeDimension ? 'max-w-[13.5rem]' : 'max-w-[24rem]'
  const aspectRatioClasses = aspectRatio
    ? `${aspectRatioClass[aspectRatio]} ${!unsetMaxDimensions ? dimensions : ''}`
    : ''

  if (!images?.length && !media?.length) return null

  // CSS fallback so the layout still reserves the right space if width/height
  // attributes are ever missing (e.g. an asset without Sanity metadata).
  const fallbackAspectRatio = (image?: Image) =>
    image?.width && image?.height ? `${image.width} / ${image.height}` : undefined

  if (images?.length === 1) {
    // single-image path stays fully synchronous — no Swiper import needed
    const imgLoading = lazy ? 'lazy' : 'eager'
    const imgPriority = lazy ? 'auto' : 'high'

    if (href) {
      return (
        <a href={href} aria-label={images[0].alt} className={className} tabIndex={-1}>
          <img
            src={images[0]?.src}
            srcSet={images[0]?.srcset}
            sizes={images[0]?.sizes}
            alt={images[0]?.alt || 'image'}
            width={images[0]?.width}
            height={images[0]?.height}
            style={aspectRatio === 'custom' ? { aspectRatio: fallbackAspectRatio(images[0]) } : undefined}
            fetchPriority={imgPriority}
            loading={imgLoading}
            className={`w-full h-full object-cover ${aspectRatioClasses}`}
          />
        </a>
      )
    }

    return (
      <img
        src={images[0]?.src}
        srcSet={images[0]?.srcset}
        sizes={images[0]?.sizes}
        alt={images[0]?.alt || 'image'}
        width={images[0]?.width}
        height={images[0]?.height}
        style={aspectRatio === 'custom' ? { aspectRatio: fallbackAspectRatio(images[0]) } : undefined}
        fetchPriority={imgPriority}
        loading={imgLoading}
        className={`w-full h-full object-cover ${aspectRatioClasses} ${className}`}
      />
    )
  }

  if (media.length === 1) {
    return <OrMedia {...media[0]} />
  }

  return (
    <SwiperEngine
      swiperRef={swiper}
      media={media}
      images={images}
      href={href}
      lazy={lazy}
      aspectRatio={aspectRatio}
      className={`${className} ${aspectRatioClasses}`}
      beginning={beginning}
      end={end}
      onBeginningChange={setBeginning}
      onEndChange={setEnd}
      onSlideChange={onSlideChange}
      onClick={onClick}
    />
  )
}
